import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { connectWallet, getReadContract, getWriteContract, ROLE_KEYS, ROLES, ROLE_LABELS, isAddress, parseUnits } from "@/lib/web3";
import * as apiClient from "@/lib/api";
import { decodeContractError } from "@/lib/errors";

const AppContext = createContext(null);

const DEFAULT_TOKEN = {
  name: "PIO USD",
  symbol: "PUSD",
  decimals: 6,
  totalSupply: 1_250_000n * 10n ** 6n,
  maxSupply: 100_000_000n * 10n ** 6n,
  paused: false,
  proxy: "0x0000000000000000000000000000000000000000",
  implementation: "0x0000000000000000000000000000000000000000",
};

const DEMO_BLACKLIST = [
  { address: "0xb1ack11sted1111111111111111111111111111ab", reason: "OFAC sanctioned wallet", active: true, updated_at: new Date(Date.now() - 86400000 * 3).toISOString() },
];

const DEMO_ROLES = [
  { role: ROLES.DEFAULT_ADMIN_ROLE, address: "0x0wner000000000000000000000000000000000001", note: "Foundation Safe", granted: true, updated_at: new Date().toISOString() },
  { role: ROLES.UPGRADER_ROLE, address: "0xupgrader000000000000000000000000000000002", note: "Upgrade Safe (4/7)", granted: true, updated_at: new Date().toISOString() },
  { role: ROLES.MASTER_MINTER_ROLE, address: "0xmasterminter00000000000000000000000000003", note: "Treasury ops", granted: true, updated_at: new Date().toISOString() },
  { role: ROLES.MINTER_ROLE, address: "0xminter00000000000000000000000000000000004", note: "Allowance: 500,000 PUSD", granted: true, updated_at: new Date().toISOString() },
  { role: ROLES.PAUSER_ROLE, address: "0xpauser00000000000000000000000000000000005", note: "Security Safe", granted: true, updated_at: new Date().toISOString() },
  { role: ROLES.BLACKLISTER_ROLE, address: "0xblacklister0000000000000000000000000000006", note: "Compliance Safe", granted: true, updated_at: new Date().toISOString() },
  { role: ROLES.BURNER_ROLE, address: "0xburner00000000000000000000000000000000007", note: "Redemption desk", granted: true, updated_at: new Date().toISOString() },
];

const DEMO_AUDIT = [
  { id: "1", event_type: "Mint", actor: "0xminter00000000000000000000000000000000004", subject: "0xa11ce00000000000000000000000000000000008", amount: "250000000000", tx_hash: "0xdeadbeef", timestamp: new Date(Date.now() - 3600_000).toISOString(), metadata: {} },
  { id: "2", event_type: "Burn", actor: "0xburner00000000000000000000000000000000007", subject: "0xburner00000000000000000000000000000000007", amount: "10000000000", tx_hash: "0xbeefdead", timestamp: new Date(Date.now() - 5400_000).toISOString(), metadata: {} },
  { id: "3", event_type: "RoleGranted", actor: "0x0wner000000000000000000000000000000000001", subject: "0xminter00000000000000000000000000000000004", tx_hash: "0xabc", timestamp: new Date(Date.now() - 7200_000).toISOString(), metadata: { role: "MINTER_ROLE" } },
  { id: "4", event_type: "Blacklisted", actor: "0xblacklister0000000000000000000000000000006", subject: "0xb1ack11sted1111111111111111111111111111ab", tx_hash: "0xdef", timestamp: new Date(Date.now() - 86400_000).toISOString(), metadata: {} },
];

export function AppProvider({ children }) {
  const [wallet, setWallet] = useState(null); // { address, chainId }
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState(DEFAULT_TOKEN);
  const [audit, setAudit] = useState([]);
  const [roles, setRoles] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [stats, setStats] = useState({ events_total: 0, mints: 0, burns: 0, blacklist_active: 0, role_holders: 0 });
  const [loading, setLoading] = useState(true);

  const live = useMemo(
    () => Boolean(config?.proxy_address && isAddress(config.proxy_address)),
    [config]
  );

  const refreshAll = useCallback(async () => {
    try {
      const [cfg, a, r, b, s] = await Promise.all([
        apiClient.getConfig(),
        apiClient.listAudit({ limit: 200 }),
        apiClient.listRoles(),
        apiClient.listBlacklist(),
        apiClient.getStats(),
      ]);
      setConfig(cfg);
      setAudit(a.length ? a : DEMO_AUDIT);
      setRoles(r.length ? r : DEMO_ROLES);
      setBlacklist(b.length ? b : DEMO_BLACKLIST);
      setStats(s);
    } catch (e) {
      // Backend not ready — fall back to demo data
      setAudit(DEMO_AUDIT);
      setRoles(DEMO_ROLES);
      setBlacklist(DEMO_BLACKLIST);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // ---- Wallet ----
  const connect = useCallback(async () => {
    try {
      const { address, chainId } = await connectWallet({
        chainId: config?.chain_id,
        rpcUrl: config?.rpc_url,
        networkName: config?.network_name,
        explorerUrl: config?.explorer_url,
      });
      setWallet({ address, chainId });
      if (config?.chain_id && chainId !== config.chain_id) {
        toast.warning("Wallet on different chain", {
          description: `Connected to chain ${chainId}, but config expects ${config.chain_id}. Switch network in your wallet.`,
        });
      } else {
        toast.success("Wallet connected", { description: address });
      }
      return true;
    } catch (e) {
      const code = e?.code;
      if (code === "NO_WALLET" || code === "NO_METAMASK") {
        toast.error("MetaMask not detected", {
          description: e.message,
          action: {
            label: "Install MetaMask",
            onClick: () => window.open("https://metamask.io/download/", "_blank"),
          },
          duration: 8000,
        });
      } else if (code === 4001) {
        toast.error("Wallet connect failed", { description: "Request rejected in wallet." });
      } else {
        toast.error("Wallet connect failed", { description: e?.message || "Unknown error" });
      }
      return false;
    }
  }, [config]);

  const disconnect = useCallback(() => {
    setWallet(null);
    toast.message("Wallet disconnected");
  }, []);

  // Listen for wallet account/chain changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.ethereum) return;
    const onAccounts = (accs) => {
      if (!accs || accs.length === 0) setWallet(null);
      else setWallet((w) => (w ? { ...w, address: accs[0] } : { address: accs[0], chainId: w?.chainId }));
    };
    const onChain = (hex) => {
      const next = parseInt(hex, 16);
      setWallet((w) => (w ? { ...w, chainId: next } : null));
    };
    window.ethereum.on?.("accountsChanged", onAccounts);
    window.ethereum.on?.("chainChanged", onChain);
    return () => {
      window.ethereum.removeListener?.("accountsChanged", onAccounts);
      window.ethereum.removeListener?.("chainChanged", onChain);
    };
  }, []);

  // Silence noisy unhandled rejections from hijacking wallet extensions
  // (e.g. Coinbase Wallet's inpage.js throwing "MetaMask extension not found")
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onRej = (e) => {
      const msg = String(e?.reason?.message || e?.reason || "");
      if (
        /MetaMask.*not (found|installed)/i.test(msg) ||
        /Failed to connect to MetaMask/i.test(msg) ||
        /inpage\.js/i.test(String(e?.reason?.stack || ""))
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", onRej);
    return () => window.removeEventListener("unhandledrejection", onRej);
  }, []);

  // ---- Live contract reads ----
  const refreshToken = useCallback(async (opts = {}) => {
    if (!live) return;
    const { silent = false } = opts;
    try {
      const ethers = await import("ethers");
      let provider;
      if (wallet?.address && window?.ethereum) {
        // Use the wallet's provider — MetaMask handles RPC, no browser CORS.
        provider = new ethers.BrowserProvider(window.ethereum);
      } else {
        // No wallet: read through our backend RPC proxy to avoid CORS.
        // The backend forwards to config.rpc_url server-side.
        const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/rpc`;
        provider = new ethers.JsonRpcProvider(proxyUrl, config?.chain_id || undefined, {
          staticNetwork: true,
          batchMaxCount: 1,
        });
      }
      const c = getReadContract(provider, config.proxy_address);
      const [name, symbol, decimals, totalSupply, maxSupply, paused] = await Promise.all([
        c.name(), c.symbol(), c.decimals(), c.totalSupply(), c.maxSupply(), c.paused(),
      ]);
      setToken({
        name, symbol, decimals: Number(decimals),
        totalSupply, maxSupply, paused,
        proxy: config.proxy_address,
        implementation: config.implementation_address || "0x0",
      });
    } catch (e) {
      console.warn("refreshToken failed:", e);
      if (!silent) {
        toast.error("Failed to read token state", { description: e?.shortMessage || e?.message || "RPC error" });
      }
    }
  }, [live, config, wallet]);

  useEffect(() => { refreshToken({ silent: true }); }, [refreshToken]);

  // ---- Audit log helper ----
  const logEvent = useCallback(async (payload) => {
    try {
      const evt = await apiClient.createAudit({
        ...payload,
        network: config?.network_name || "Demo",
      });
      setAudit((prev) => [evt, ...prev].slice(0, 500));
      const s = await apiClient.getStats();
      setStats(s);
      return evt;
    } catch (e) {
      // demo fallback
      const evt = { id: String(Date.now()), timestamp: new Date().toISOString(), metadata: {}, ...payload };
      setAudit((prev) => [evt, ...prev].slice(0, 500));
      return evt;
    }
  }, [config]);

  // ---- Actions: live or demo-simulated ----
  const requireRoleAddr = () => wallet?.address || "0xdemoOperator00000000000000000000000000000";

  const doMint = useCallback(async ({ to, amount }) => {
    if (!isAddress(to)) throw new Error("Invalid recipient address");
    const raw = parseUnits(String(amount), token.decimals);
    if (live) {
      const { BrowserProvider } = await import("ethers");
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const c = getWriteContract(signer, config.proxy_address);
      const tx = await c.mint(to, raw);
      await tx.wait();
      await logEvent({ event_type: "Mint", actor: wallet.address, subject: to, amount: raw.toString(), tx_hash: tx.hash });
      await refreshToken();
      toast.success("Mint confirmed", { description: tx.hash });
    } else {
      setToken((t) => ({ ...t, totalSupply: t.totalSupply + raw }));
      await logEvent({ event_type: "Mint", actor: requireRoleAddr(), subject: to, amount: raw.toString(), tx_hash: `0xsim${Date.now().toString(16)}` });
      toast.success("Mint simulated (demo mode)");
    }
  }, [live, config, wallet, token.decimals, logEvent, refreshToken]);

  const doBurn = useCallback(async ({ from, amount }) => {
    const raw = parseUnits(String(amount), token.decimals);
    if (live) {
      const { BrowserProvider } = await import("ethers");
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const c = getWriteContract(signer, config.proxy_address);
      let tx;
      if (from && from.toLowerCase() !== wallet.address.toLowerCase()) {
        tx = await c.burnFrom(from, raw);
      } else {
        tx = await c.burn(raw);
      }
      await tx.wait();
      await logEvent({ event_type: "Burn", actor: wallet.address, subject: from || wallet.address, amount: raw.toString(), tx_hash: tx.hash });
      await refreshToken();
      toast.success("Burn confirmed", { description: tx.hash });
    } else {
      setToken((t) => ({ ...t, totalSupply: t.totalSupply - raw }));
      await logEvent({ event_type: "Burn", actor: requireRoleAddr(), subject: from || requireRoleAddr(), amount: raw.toString(), tx_hash: `0xsim${Date.now().toString(16)}` });
      toast.success("Burn simulated (demo mode)");
    }
  }, [live, config, wallet, token.decimals, logEvent, refreshToken]);

  const doPause = useCallback(async (next) => {
    if (live) {
      try {
        const { BrowserProvider } = await import("ethers");
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const c = getWriteContract(signer, config.proxy_address);
        const tx = next ? await c.pause() : await c.unpause();
        await tx.wait();
        await logEvent({ event_type: next ? "Paused" : "Unpaused", actor: wallet.address, tx_hash: tx.hash });
        await refreshToken();
        toast.success(next ? "Contract paused" : "Contract unpaused", { description: tx.hash });
      } catch (e) {
        toast.error(next ? "Pause failed" : "Unpause failed", { description: decodeContractError(e), duration: 8000 });
        e.__handled = true;
        throw e;
      }
    } else {
      setToken((t) => ({ ...t, paused: next }));
      await logEvent({ event_type: next ? "Paused" : "Unpaused", actor: requireRoleAddr(), tx_hash: `0xsim${Date.now().toString(16)}` });
      toast.success(next ? "Pause simulated" : "Unpause simulated");
    }
  }, [live, config, wallet, logEvent, refreshToken]);

  const doBlacklist = useCallback(async ({ address, active, reason }) => {
    if (!isAddress(address)) throw new Error("Invalid address");
    if (live) {
      try {
        const { BrowserProvider } = await import("ethers");
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const c = getWriteContract(signer, config.proxy_address);
        const tx = active ? await c.blacklist(address) : await c.unBlacklist(address);
        await tx.wait();
        await apiClient.upsertBlacklist({ address, active, reason });
        await logEvent({ event_type: active ? "Blacklisted" : "UnBlacklisted", actor: wallet.address, subject: address, tx_hash: tx.hash, metadata: { reason } });
        toast.success(active ? "Address blacklisted" : "Address unblacklisted");
      } catch (e) {
        toast.error(active ? "Blacklist failed" : "Unblacklist failed", { description: decodeContractError(e), duration: 8000 });
        e.__handled = true;
        throw e;
      }
    } else {
      const rec = await apiClient.upsertBlacklist({ address, active, reason }).catch(() => ({
        address, active, reason, updated_at: new Date().toISOString(), id: String(Date.now()),
      }));
      setBlacklist((prev) => {
        const others = prev.filter((p) => p.address?.toLowerCase() !== address.toLowerCase());
        return [rec, ...others];
      });
      await logEvent({ event_type: active ? "Blacklisted" : "UnBlacklisted", actor: requireRoleAddr(), subject: address, metadata: { reason }, tx_hash: `0xsim${Date.now().toString(16)}` });
      toast.success(active ? "Blacklist simulated" : "Unblacklist simulated");
    }
    const list = await apiClient.listBlacklist().catch(() => null);
    if (list) setBlacklist(list);
  }, [live, config, wallet, logEvent]);

  const doRole = useCallback(async ({ role, address, granted, note }) => {
    if (!isAddress(address)) throw new Error("Invalid address");
    if (live) {
      try {
        const { BrowserProvider } = await import("ethers");
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const c = getWriteContract(signer, config.proxy_address);
        const tx = granted ? await c.grantRole(role, address) : await c.revokeRole(role, address);
        await tx.wait();
        await apiClient.upsertRole({ role, address, granted, note });
        await logEvent({ event_type: granted ? "RoleGranted" : "RoleRevoked", actor: wallet.address, subject: address, tx_hash: tx.hash, metadata: { role: ROLE_LABELS[role] } });
        toast.success(granted ? "Role granted" : "Role revoked");
      } catch (e) {
        toast.error(granted ? "Grant failed" : "Revoke failed", { description: decodeContractError(e), duration: 8000 });
        e.__handled = true;
        throw e;
      }
    } else {
      const rec = await apiClient.upsertRole({ role, address, granted, note }).catch(() => ({
        role, address, granted, note, updated_at: new Date().toISOString(), id: String(Date.now()),
      }));
      setRoles((prev) => {
        const others = prev.filter((p) => !(p.role === role && p.address?.toLowerCase() === address.toLowerCase()));
        return [rec, ...others];
      });
      await logEvent({ event_type: granted ? "RoleGranted" : "RoleRevoked", actor: requireRoleAddr(), subject: address, metadata: { role: ROLE_LABELS[role] }, tx_hash: `0xsim${Date.now().toString(16)}` });
      toast.success(granted ? "Role grant simulated" : "Role revoke simulated");
    }
    const r = await apiClient.listRoles().catch(() => null);
    if (r) setRoles(r);
  }, [live, config, wallet, logEvent]);

  const doConfigureMinter = useCallback(async ({ minter, allowance }) => {
    if (!isAddress(minter)) throw new Error("Invalid minter address");
    const raw = parseUnits(String(allowance), token.decimals);
    if (live) {
      const { BrowserProvider } = await import("ethers");
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const c = getWriteContract(signer, config.proxy_address);
      const tx = await c.configureMinter(minter, raw);
      await tx.wait();
      await apiClient.upsertRole({ role: ROLES.MINTER_ROLE, address: minter, granted: true, note: `Allowance: ${allowance} ${token.symbol}` });
      await logEvent({ event_type: "MinterConfigured", actor: wallet.address, subject: minter, amount: raw.toString(), tx_hash: tx.hash });
      toast.success("Minter configured");
    } else {
      const rec = await apiClient.upsertRole({ role: ROLES.MINTER_ROLE, address: minter, granted: true, note: `Allowance: ${allowance} ${token.symbol}` }).catch(() => ({
        role: ROLES.MINTER_ROLE, address: minter, granted: true, note: `Allowance: ${allowance} ${token.symbol}`, updated_at: new Date().toISOString(), id: String(Date.now()),
      }));
      setRoles((prev) => {
        const others = prev.filter((p) => !(p.role === ROLES.MINTER_ROLE && p.address?.toLowerCase() === minter.toLowerCase()));
        return [rec, ...others];
      });
      await logEvent({ event_type: "MinterConfigured", actor: requireRoleAddr(), subject: minter, amount: raw.toString(), tx_hash: `0xsim${Date.now().toString(16)}` });
      toast.success("Minter config simulated");
    }
  }, [live, config, wallet, token.decimals, token.symbol, logEvent]);

  const updateConfig = useCallback(async (payload) => {
    const cfg = await apiClient.updateConfig(payload);
    setConfig(cfg);
    toast.success("Config saved");
    await refreshToken();
  }, [refreshToken]);

  const value = useMemo(() => ({
    wallet, connect, disconnect,
    config, updateConfig,
    token, refreshToken,
    audit, refreshAudit: refreshAll,
    roles, blacklist, stats,
    loading, live,
    actions: { doMint, doBurn, doPause, doBlacklist, doRole, doConfigureMinter },
  }), [wallet, connect, disconnect, config, updateConfig, token, refreshToken, audit, refreshAll, roles, blacklist, stats, loading, live, doMint, doBurn, doPause, doBlacklist, doRole, doConfigureMinter]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
};
